import React, { useEffect, useState } from "react";
import { Eye, Trash2 } from "lucide-react";
import Sidebar from "../side_bar/SidebarAdmin";
import UserViewModal from "./UserViewModal";
import avatar from "../../../assets/avatar.png";
import "./UserAccountsAdmin.css";
import {
  listUsers,
  updateUserType,
  deleteUserAccount,
  AdminUser,
} from "../../../api/admin/users";
import { useAuth } from "../../../context/AuthContext";
import formatPhone from "../../../utils/formatPhone";
import Header from "../../../components/common/Header";
import TablePagination from "../../../components/common/TablePagination";

interface UserRow {
  id: number;
  name: string;
  userType: "Administrador" | "Usuário Comum" | "Colaborador";
  avatar: string;
  rawTipo: string;
  fullName?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  institution?: string;
  course?: string;
  degree?: string;
  curriculumLink?: string;
}

const UserAccountsAdmin: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { getCsrfToken, user } = useAuth();

  // Carregar dados quando currentPage ou itemsPerPage mudar
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listUsers(getCsrfToken()!, currentPage, itemsPerPage);
        if (mounted) {
          setTotal(response.total);
          setTotalPages(response.totalPages);
          setUsers(
            response.items.map((u: AdminUser) => ({
              id: u.user_id,
              name: u.nome_completo || u.email || `User ${u.user_id}`,
              userType:
                u.tipo === "Admin"
                  ? "Administrador"
                  : u.tipo === "Comum"
                  ? "Usuário Comum"
                  : "Colaborador",
              avatar: avatar,
              rawTipo: u.tipo || "",
              fullName: u.nome_completo,
              phone: u.telefone ? formatPhone(String(u.telefone)) : undefined,
              whatsapp: u.whatsapp
                ? formatPhone(String(u.whatsapp))
                : undefined,
              email: u.email,
              institution: u.instituicao,
              course: u.curso,
              degree: u.nivel_academico,
              curriculumLink: u.link_lattes,
            }))
          );
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || "Erro ao carregar usuários");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [getCsrfToken, currentPage, itemsPerPage]);

  const getUserTypeClass = (userType: UserRow["userType"]) => {
    const typeMap: Record<string, string> = {
      Administrador: "badge-admin",
      "Usuário Comum": "badge-user",
      Colaborador: "badge-collaborator",
    };
    return typeMap[userType] || "badge-user";
  };

  const handleTypeFilter = (type: string | null) => {
    setSelectedType(type);
  };

  const handleDeleteUserClick = (userId: number) => {
    setUserToDelete(userId);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete === null) return;
    try {
      const token = getCsrfToken();
      if (token) {
        await deleteUserAccount(userToDelete, token);
      }
      setUsers((prev) => prev.filter((u) => u.id !== userToDelete));
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Erro ao excluir usuário");
    } finally {
      setShowDeleteConfirmation(false);
      setUserToDelete(null);
    }
  };

  const handleViewUser = (userId: number) => {
    const u = users.find((x) => x.id === userId);
    if (u) {
      setSelectedUser(u);
      setShowViewModal(true);
    }
  };

  const handleUserTypeChange = async (
    userId: number,
    newType: UserRow["userType"]
  ) => {
    const backendMap: Record<string, string> = {
      Administrador: "Admin",
      "Usuário Comum": "Comum",
      Colaborador: "Colaborador",
    };
    try {
      await updateUserType(userId, backendMap[newType], getCsrfToken()!);
      setUsers(
        users.map((user) =>
          user.id === userId
            ? { ...user, userType: newType, rawTipo: backendMap[newType] }
            : user
        )
      );

      // Feedback de sucesso
      setSuccessMessage(
        `Tipo de usuário alterado para ${newType} com sucesso!`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e) {
      console.error(e);
      setError("Erro ao alterar tipo de usuário");
      setTimeout(() => setError(null), 3000);
    }
  };

  const renderDeleteConfirmationModal = () => {
    if (!showDeleteConfirmation) return null;

    return (
      <div
        className="modal show d-block delete-confirmation-modal"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirmar Exclusão</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowDeleteConfirmation(false)}
              />
            </div>
            <div className="modal-body">
              <p>Deseja mesmo excluir esta conta de usuário?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmDelete}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredUsers = users.filter((item) =>
    (!selectedType || item.userType === selectedType) &&
    (item.name + " " + (item.email || "")).toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <div className="account-profile-page account-table-page">
      <Header />
      {renderDeleteConfirmationModal()}
      <UserViewModal
        user={selectedUser as any}
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedUser(null);
        }}
        onDelete={(userId) => {
          (async () => {
            try {
              await deleteUserAccount(userId, getCsrfToken()!);
            } catch (e) {
              console.error(e);
            }
          })();
          setUsers(users.filter((user) => user.id !== userId));
          setShowViewModal(false);
          setSelectedUser(null);
        }}
      />
      <div className="account-profile-layout">
        <div className="account-navigation">
          <Sidebar
            activeItem="contas-usuario"
            avatar={avatar}
            name={user?.nome_completo || "Administrador"}
          />
        </div>
        <main className="account-profile-content">
          <div className="account-page-heading"><h1>Contas de usuários</h1></div>
          <section className="account-table-panel" aria-label="Contas de usuários">
            <div className="account-table-toolbar">
              <label>Pesquisar nesta página
                <input type="search" className="form-control" placeholder="Nome ou e-mail" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
              </label>
              <label>Tipo nesta página
                <select className="form-select" value={selectedType || ""} onChange={(event) => handleTypeFilter(event.target.value || null)}>
                  <option value="">Todos</option><option value="Administrador">Administrador</option><option value="Usuário Comum">Usuário comum</option><option value="Colaborador">Colaborador</option>
                </select>
              </label>
            </div>
            {/* Mensagens de feedback */}
            {successMessage && (
              <div
                className="alert alert-success alert-dismissible fade show"
                role="alert"
              >
                {successMessage}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccessMessage(null)}
                ></button>
              </div>
            )}

            {error && (
              <div
                className="alert alert-danger alert-dismissible fade show"
                role="alert"
              >
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                ></button>
              </div>
            )}

            <div className="table-responsive" role="region" aria-label="Tabela de contas de usuários" tabIndex={0}>
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>NOMES</th>
                    <th>Tipo de usuário</th>
                    <th>AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={4}>Carregando...</td>
                    </tr>
                  )}
                  {error && !loading && (
                    <tr>
                      <td colSpan={4} className="text-danger">
                        {error}
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    !error &&
                    filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>
                          <div className="user-info">
                            <img
                              src={user.avatar}
                              alt={user.name}
                              className="user-avatar"
                            />
                            <span>{user.name}</span>
                          </div>
                        </td>
                        <td>
                          <select className={`account-role-select ${getUserTypeClass(user.userType)}`} aria-label={`Tipo de usuário de ${user.name}`} value={user.userType} onChange={(event) => handleUserTypeChange(user.id, event.target.value as UserRow["userType"])}>
                            <option value="Administrador">Administrador</option>
                            <option value="Usuário Comum">Usuário comum</option>
                            <option value="Colaborador">Colaborador</option>
                          </select>
                        </td>
                        <td><div className="account-row-actions">
                          <button
                            className="btn btn-icon"
                            title="Visualizar"
                            aria-label={`Visualizar usuário ${user.name}`}
                            onClick={() => handleViewUser(user.id)}
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="btn btn-icon text-danger"
                            title="Excluir"
                            aria-label={`Excluir usuário ${user.name}`}
                            onClick={() => handleDeleteUserClick(user.id)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div></td>
                      </tr>
                    ))}
                  {!loading && !error && filteredUsers.length === 0 && <tr><td colSpan={4} className="account-table-empty">Nenhum usuário encontrado nesta página.</td></tr>}
                </tbody>
              </table>
            </div>
            <TablePagination page={currentPage} totalPages={totalPages} pageSize={itemsPerPage} total={total} disabled={loading} onPageChange={setCurrentPage} onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }} />
          </section>
        </main>
      </div>
    </div>
  );
};

export default UserAccountsAdmin;
